import React, { useEffect, createElement } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { PageTransition } from './components/PageTransition';
import { Home } from './pages/Home';
import { FAQ } from './pages/FAQ';
import { Contact } from './pages/Contact';
import { TechnologieLaser } from './pages/TechnologieLaser';
import { AvantApres } from './pages/AvantApres';
import { Tarifs } from './pages/Tarifs';
import { Blog } from './pages/Blog';
import { BlogPost } from './pages/BlogPost';
import { MentionsLegales } from './pages/MentionsLegales';
import { PolitiqueConfidentialite } from './pages/PolitiqueConfidentialite';
import { Favicon } from './pages/Favicon';
export function App() {
  const location = useLocation();
  // Scroll to top on route change
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);
  useEffect(() => {
    // Global SEO setup
    document.documentElement.lang = 'fr';
    let viewport = document.querySelector('meta[name="viewport"]') as HTMLMetaElement;
    if (!viewport) {
      viewport = document.createElement('meta');
      viewport.name = 'viewport';
      viewport.content = 'width=device-width, initial-scale=1';
      document.head.appendChild(viewport);
    }
    // SEO: Robots
    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    robots.content = 'index, follow';
    // SEO: Open Graph tags
    const ogTags: Record<string, string> = {
      'og:title': 'Détatouage à Martigny | Laser Picoseconde & Q-Switched | Dispel Your Tattoo Valais',
      'og:description': 'Détatouage professionnel à Martigny, Valais. Spécialiste du laser picoseconde et Q-Switched pour enlever ou éclaircir votre tatouage en toute sécurité. Consultations transparentes et résultats maîtrisés au cœur du Valais.',
      'og:type': 'website',
      'og:url': 'https://www.dispelyourtattoo.ch',
      'og:locale': 'fr_CH',
      'og:site_name': 'Dispel Your Tattoo',
      'og:image': 'https://cdn.magicpatterns.com/uploads/c7DtgUV4minxineBKqyaMP/IMG_2681-PROOF-THOMAS-MASOTTI.jpg',
      'og:image:width': '1200',
      'og:image:height': '630',
      'twitter:card': 'summary_large_image',
      'twitter:site': '@dispelyourtattoo'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      // Handle both property (OG) and name (Twitter) attributes
      const attr = property.startsWith('twitter:') ? 'name' : 'property';
      let tag = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
    // SEO: JSON-LD Structured Data
    const schemaId = 'dispel-schema-ld';
    let existingSchema = document.getElementById(schemaId);
    if (existingSchema) existingSchema.remove();
    const schema = document.createElement('script');
    schema.id = schemaId;
    schema.type = 'application/ld+json';
    schema.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [{
        '@type': 'LocalBusiness',
        '@id': 'https://www.dispelyourtattoo.ch/#business',
        name: 'Dispel Your Tattoo',
        description: 'Studio spécialisé en détatouage laser à Martigny, Valais. Technologies picoseconde et Q-Switched.',
        url: 'https://www.dispelyourtattoo.ch',
        image: 'https://cdn.magicpatterns.com/uploads/c7DtgUV4minxineBKqyaMP/IMG_2681-PROOF-THOMAS-MASOTTI.jpg',
        telephone: '+41791026945',
        email: 'dispeltattoo@gmail.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Rue de la Délèze 2',
          postalCode: '1920',
          addressLocality: 'Martigny',
          addressRegion: 'Valais',
          addressCountry: 'CH'
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 46.0986,
          longitude: 7.0728
        },
        priceRange: '$$',
        openingHoursSpecification: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '18:00'
        },
        areaServed: {
          '@type': 'Place',
          name: 'Valais, Suisse'
        },
        sameAs: []
      }, {
        '@type': 'Service',
        '@id': 'https://www.dispelyourtattoo.ch/#service',
        name: 'Détatouage Laser',
        description: 'Effacement professionnel de tatouages par laser picoseconde et Q-Switched à Martigny.',
        provider: {
          '@id': 'https://www.dispelyourtattoo.ch/#business'
        },
        areaServed: {
          '@type': 'Place',
          name: 'Valais, Suisse'
        },
        serviceType: 'Détatouage laser'
      }, {
        '@type': 'WebSite',
        '@id': 'https://www.dispelyourtattoo.ch/#website',
        url: 'https://www.dispelyourtattoo.ch',
        name: 'Dispel Your Tattoo',
        inLanguage: 'fr-CH',
        publisher: {
          '@id': 'https://www.dispelyourtattoo.ch/#business'
        }
      }]
    });
    document.head.appendChild(schema);
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);
  return <div className="min-h-screen bg-brand-dark text-brand-light selection:bg-brand-light selection:text-brand-dark" data-id="element-0">
      <Navbar data-id="element-1" />
      <AnimatePresence mode="wait" data-id="element-2">
        <Routes location={location} key={location.pathname} data-id="element-3">
          <Route path="/" element={<PageTransition data-id="element-5">
                <Home data-id="element-6" />
              </PageTransition>} data-id="element-4" />
          <Route path="/technologie-laser" element={<PageTransition data-id="element-8">
                <TechnologieLaser data-id="element-9" />
              </PageTransition>} data-id="element-7" />
          <Route path="/avant-apres" element={<PageTransition data-id="element-11">
                <AvantApres data-id="element-12" />
              </PageTransition>} data-id="element-10" />
          <Route path="/tarifs" element={<PageTransition data-id="element-14">
                <Tarifs data-id="element-15" />
              </PageTransition>} data-id="element-13" />
          <Route path="/faq" element={<PageTransition data-id="element-17">
                <FAQ data-id="element-18" />
              </PageTransition>} data-id="element-16" />
          <Route path="/blog" element={<PageTransition data-id="element-20">
                <Blog data-id="element-21" />
              </PageTransition>} data-id="element-19" />
          <Route path="/blog/:slug" element={<PageTransition data-id="element-23">
                <BlogPost data-id="element-24" />
              </PageTransition>} data-id="element-22" />
          <Route path="/contact" element={<PageTransition data-id="element-26">
                <Contact data-id="element-27" />
              </PageTransition>} data-id="element-25" />
          <Route path="/mentions-legales" element={<PageTransition data-id="element-29">
                <MentionsLegales data-id="element-30" />
              </PageTransition>} data-id="element-28" />
          <Route path="/politique-de-confidentialite" element={<PageTransition data-id="element-32">
                <PolitiqueConfidentialite data-id="element-33" />
              </PageTransition>} data-id="element-31" />
          <Route path="/favicon" element={<PageTransition data-id="element-35">
                <Favicon data-id="element-36" />
              </PageTransition>} data-id="element-34" />
        </Routes>
      </AnimatePresence>
      <Footer data-id="element-37" />
    </div>;
}